import {
  ConferenceRecordingMeta,
  ConferenceRecordingMetaRequest,
  ConferenceRecordingMetaResponse,
  CallRecordingURLResponse,
} from 'src/models/callRecordings';
import { erebus } from 'boot/axios';
import { AxiosResponse } from 'axios';
import { ErrorHandlingService, ErrorType } from 'src/services/errorHandlingService';

export default class CallRecordingService {
  errorHandlingService = new ErrorHandlingService();

  async getCallRecordingSignedURL(recordingID: string): Promise<CallRecordingURLResponse | null> {
    try {
      const response: AxiosResponse<CallRecordingURLResponse> = await erebus.get(
        `/ivr/conference-recording/${recordingID}`,
      );

      return response.data;
    } catch (error) {
      const apiError = this.errorHandlingService.parseApiError(error);

      if (apiError.type === ErrorType.NotFound) {
        console.warn(`Recording ${recordingID} not found`);
        return null;
      }

      throw apiError;
    }
  }

  async fetchCallRecording(
    recordingID: string,
  ): Promise<{ data: ArrayBuffer; contentType: string } | null> {
    try {
      const signedUrlResponse = await this.getCallRecordingSignedURL(recordingID);

      if (!signedUrlResponse || !signedUrlResponse.url) {
        return null;
      }

      console.log('Fetching recording from signed URL');

      const response = await fetch(signedUrlResponse.url);

      if (!response.ok) {
        throw new Error(`Failed to fetch recording: ${response.status} ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      // Use content type from backend response, fallback to response header, then default
      const contentType =
        signedUrlResponse.contentType || response.headers.get('content-type') || 'audio/wav';

      return { data: arrayBuffer, contentType };
    } catch (error) {
      const apiError = this.errorHandlingService.parseApiError(error);

      if (apiError.type === ErrorType.NotFound) {
        console.warn(`Recording ${recordingID} not found`);
        return null;
      }

      throw apiError;
    }
  }

  async listCallRecordings(
    params: ConferenceRecordingMetaRequest,
  ): Promise<ConferenceRecordingMeta[]> {
    this.validateParams(params);
    const res: AxiosResponse<ConferenceRecordingMetaResponse[]> = await erebus.get(
      '/ivr/conference-recording',
      {
        params: {
          ...params,
        },
      },
    );
    // Convert the API response to the domain type with Date objects
    return res.data.map((recording: ConferenceRecordingMetaResponse) => ({
      ID: recording.ID || '',
      callID: recording.callID,
      caseID: recording.caseID,
      activationID: recording.activationID,
      clientID: recording.clientID,
      locationID: recording.locationID,
      recordingSid: recording.recordingSid,
      conferenceSid: recording.conferenceSid,
      routingAttemptID: recording.routingAttemptID,
      contentType: recording.contentType || 'audio/wav',
      duration: recording.duration,
      recordingStartTime: new Date(recording.recordingStartTime || 0),
      createdAt: new Date(recording.createdAt || 0),
    }));
  }

  async getCallRecordingByID(ID: string): Promise<ConferenceRecordingMeta> {
    const params = { ID };
    const recordings = await this.listCallRecordings(params);
    if (recordings.length === 0) {
      throw new Error(`Recording with ID ${ID} not found.`);
    }
    if (recordings.length > 1) {
      throw new Error(`Multiple recordings found with ID ${ID}.`);
    }
    return recordings[0];
  }

  async getCallRecordingsByCaseID(caseID: number): Promise<ConferenceRecordingMeta[]> {
    const params = { caseID };
    const recordings = await this.listCallRecordings(params);
    if (recordings.length === 0) {
      return [];
    }
    return recordings;
  }

  async getCallRecordingsByActivationID(activationID: number): Promise<ConferenceRecordingMeta[]> {
    const params = { activationID };
    const recordings = await this.listCallRecordings(params);
    if (recordings.length === 0) {
      return [];
    }
    return recordings;
  }

  private validateParams(params: ConferenceRecordingMetaRequest): void {
    if (!params.ID && !params.caseID && !params.activationID) {
      throw new Error('At least one of ID, caseID, or activationID must be provided.');
    }
  }
}
